import { useState, useCallback, useEffect, useRef } from 'react';

// 声明阿里云验证码全局类型
declare global {
  interface Window {
    initAliyunCaptcha?: (config: {
      SceneId: string;
      mode: string;
      element: string;
      button: string;
      success: (captchaVerifyParam: string) => void;
      fail: (error: any) => void;
      getInstance: (instance: any) => void;
      slideStyle?: { width: number; height: number };
      language?: string;
    }) => void;
    AliyunCaptchaConfig?: {
      region: string;
      prefix: string;
    };
  }
}

interface UseAliyunCaptchaOptions {
  elementId: string; // 验证码渲染容器元素ID
  buttonId: string; // 触发验证码按钮元素ID
  onSuccess?: (captchaVerifyParam: string) => void; // 验证成功回调
  onError?: (error: any) => void; // 验证失败回调
  enabled?: boolean; // 是否启用验证码
  slideStyle?: { width: number; height: number }; // 滑块样式
  language?: string; // 语言
}

interface UseAliyunCaptchaReturn {
  captchaInstance: any; // 验证码实例
  captchaVerified: boolean; // 是否已验证
  captchaVerifyParam: string; // 验证参数
  isReady: boolean; // 是否已初始化
  initCaptcha: () => void; // 初始化验证码
  triggerCaptcha: () => void; // 触发验证码
  resetCaptcha: () => void; // 重置验证码
  reinitCaptcha: () => void; // 重新初始化验证码
}

/**
 * 阿里云验证码2.0 Hook
 * @param options 配置选项
 * @returns 验证码相关状态和方法
 */
export const useAliyunCaptcha = (options: UseAliyunCaptchaOptions): UseAliyunCaptchaReturn => {
  const {
    elementId,
    buttonId,
    onSuccess,
    onError,
    enabled = true,
    slideStyle = { width: 360, height: 40 },
    language = 'cn',
  } = options;

  const [captchaInstance, setCaptchaInstance] = useState<any>(null);
  const [captchaVerified, setCaptchaVerified] = useState<boolean>(false);
  const [captchaVerifyParam, setCaptchaVerifyParam] = useState<string>('');
  const [isReady, setIsReady] = useState<boolean>(false);
  const initRef = useRef<boolean>(false); // 防止重复初始化
  const onSuccessRef = useRef(onSuccess); // 保存最新的成功回调
  const onErrorRef = useRef(onError); // 保存最新的错误回调

  // 更新回调函数的引用
  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  }, [onSuccess, onError]);

  // 初始化阿里云验证码
  const initCaptcha = useCallback(() => {
    if (!enabled) {
      return;
    }

    if (initRef.current) {
      console.warn('验证码已初始化，跳过重复初始化');
      return;
    }

    if (!window.initAliyunCaptcha) {
      console.warn('阿里云验证码SDK未加载');
      return;
    }

    const sceneId = process.env.REACT_APP_ALIYUN_CAPTCHA_SCENE || '';
    if (!sceneId) {
      console.warn('未配置阿里云验证码场景ID');
      return;
    }

    // 确保全局配置已设置
    if (!window.AliyunCaptchaConfig) {
      window.AliyunCaptchaConfig = {
        region: process.env.REACT_APP_ALIYUN_CAPTCHA_REGION || 'cn',
        prefix: '',
      };
    }

    // 设置prefix（如果尚未设置）
    const prefix = process.env.REACT_APP_ALIYUN_CAPTCHA_PREFIX || '';
    if (prefix && (!window.AliyunCaptchaConfig.prefix || window.AliyunCaptchaConfig.prefix === 'undefined')) {
      window.AliyunCaptchaConfig.prefix = prefix;
      window.AliyunCaptchaConfig.region = process.env.REACT_APP_ALIYUN_CAPTCHA_REGION || 'cn';
    }

    // 如果prefix仍然为空或undefined，不初始化验证码
    if (!window.AliyunCaptchaConfig.prefix || window.AliyunCaptchaConfig.prefix === 'undefined') {
      console.warn('未配置阿里云验证码prefix，跳过验证码初始化');
      return;
    }

    try {
      window.initAliyunCaptcha({
        SceneId: sceneId,
        mode: 'popup',
        element: `#${elementId}`,
        button: `#${buttonId}`,
        success: (param: string) => {
          console.log('验证成功，验证参数：', param);
          setCaptchaVerifyParam(param);
          setCaptchaVerified(true);
          onSuccessRef.current?.(param);
        },
        fail: (error: any) => {
          console.error('验证失败：', error);
          setCaptchaVerified(false);
          setCaptchaVerifyParam('');
          onErrorRef.current?.(error);
        },
        getInstance: (instance: any) => {
          setCaptchaInstance(instance);
          setIsReady(true);
          initRef.current = true;
        },
        slideStyle,
        language,
      });
    } catch (error) {
      console.error('初始化验证码失败:', error);
      onError?.(error);
    }
  }, [enabled, elementId, buttonId, onSuccess, onError, slideStyle, language]);

  // 触发验证码
  const triggerCaptcha = useCallback(() => {
    if (!isReady || !captchaInstance) {
      // 如果未初始化，先初始化
      initCaptcha();
      // 等待初始化完成后再触发
      setTimeout(() => {
        const button = document.getElementById(buttonId);
        if (button) {
          button.click();
        }
      }, 500);
      return;
    }

    // 触发验证码弹窗
    const button = document.getElementById(buttonId);
    if (button) {
      button.click();
    }
  }, [isReady, captchaInstance, buttonId, initCaptcha]);

  // 重置验证码
  const resetCaptcha = useCallback(() => {
    setCaptchaVerified(false);
    setCaptchaVerifyParam('');
    initRef.current = false;
    setIsReady(false);
    setCaptchaInstance(null);
  }, []);

  // 重新初始化验证码（用于验证失败或成功后重置）
  const reinitCaptcha = useCallback(() => {
    resetCaptcha();
    setTimeout(() => {
      initCaptcha();
    }, 1000);
  }, [resetCaptcha, initCaptcha]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // 等待SDK加载完成
    const checkSDK = setInterval(() => {
      if (window.initAliyunCaptcha) {
        clearInterval(checkSDK);
      }
    }, 100);

    // 5秒后停止检查
    const timeout = setTimeout(() => {
      clearInterval(checkSDK);
    }, 5000);

    return () => {
      clearInterval(checkSDK);
      clearTimeout(timeout);
    };
  }, [enabled]);

  return {
    captchaInstance,
    captchaVerified,
    captchaVerifyParam,
    isReady,
    initCaptcha,
    triggerCaptcha,
    resetCaptcha,
    reinitCaptcha,
  };
};
